import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { useFamilyStore } from '../../stores/familyStore';
import { PrimaryParentOnly } from '../security/PermissionGate';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useEncryptionContext } from '../../contexts/EncryptionContext';

interface Child {
  id: string;
  name: string;
  family_id: string;
  created_at: string;
}

/**
 * Component displaying the list of children with management options
 */
export const ChildrenList: React.FC = () => {
  const { family, children, fetchChildren, addChild, isLoading, error } = useFamilyStore();
  const entitlements = useEntitlements(family?.id);
  const [showAddForm, setShowAddForm] = useState(false);
  const [childName, setChildName] = useState('');
  const navigate = useNavigate();


  useEffect(() => {
    if (family) {
      fetchChildren(family.id);
    }
  }, [family, fetchChildren]);

  const handleAddChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!family || !childName) return;

    if (children.length >= entitlements.maxChildren) {
      alert(`Child limit in your plan has been reached (max. ${entitlements.maxChildren}). Change plan to add another child.`);
      return;
    }

    const newChildId = await addChild(childName);
    setChildName('');
    setShowAddForm(false);

    // After adding a child from general settings - navigate once to that child's calendar
    if (newChildId) {
      navigate('/calendar', {
        state: { focusChildId: newChildId }
      });
    }
  };

  if (!family) {
    return <div className="text-center py-4">No active family</div>;
  }

  if (isLoading) {
    return <div className="text-center py-4">Loading children...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Children / Dependents</h2>
        <PrimaryParentOnly>
          <button
            onClick={() => {
              if (!showAddForm && children.length >= entitlements.maxChildren) {
                alert(`You can have a maximum of ${entitlements.maxChildren} ${entitlements.maxChildren === 1 ? 'child' : 'children'} in your plan. Upgrade to add more.`);
                return;
              }
              setShowAddForm(!showAddForm);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center"
          >
            <span className="mr-1">{showAddForm ? '−' : '+'}</span>
            {showAddForm ? 'Cancel' : 'Add'}
          </button>
        </PrimaryParentOnly>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddChild} className="bg-blue-50 p-3 rounded-lg border border-blue-100 mb-6">
          <div className="flex items-end gap-3">
            <div className="flex-grow">
              <label htmlFor="childName" className="block text-xs font-medium text-blue-700 mb-1">
                Child's name
              </label>
              <input
                id="childName"
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                className="w-full px-3 py-1.5 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                required
                placeholder="Enter name..."
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !childName}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-md transition-colors flex items-center text-sm mb-[1px]"
            >
              {isLoading ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span>Adding...</span>
                </>
              ) : (
                'Add'
              )}
            </button>
          </div>
        </form>
      )}

      {children.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No children / dependents in the family. {!showAddForm && "Click \"Add\" to add the first child / dependent."}
        </div>
      ) : (
        <div className="space-y-4">
          {children.map((child) => (
            <ChildItem key={child.id} child={child} />
          ))}
          {children.length >= entitlements.maxChildren && (
            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              Child limit reached in your plan. <a href="/pricing" className="text-blue-600 underline">View plans</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Single child item component
 */
const ChildItem: React.FC<{ child: Child }> = ({ child }) => {
  const { updateChild, removeChild, fetchChildren } = useFamilyStore();
  const { family } = useFamilyStore();
  const { decryptIfNeeded, isReady: e2eeReady } = useEncryptionContext();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(child.name);
  const [name, setName] = useState(child.name);

  // Decrypt child name for display
  useEffect(() => {
    if (!e2eeReady) return;
    decryptIfNeeded(child.name).then((decrypted) => {
      setDisplayName(decrypted);
      if (!isEditing) setName(decrypted);
    });
  }, [child.name, e2eeReady, decryptIfNeeded, isEditing]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = async () => {
    setIsLoading(true);

    const updateData: { name?: string } = {};

    if (name.trim() !== displayName) {
      updateData.name = name.trim();
    }

    logger.debug('🔄 Saving data:', updateData);

    const success = await updateChild(child.id, updateData);

    if (success) {
      setIsEditing(false);
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    const success = await removeChild(child.id);
    if (success && family) {
      await fetchChildren(family.id);
    }
    setIsLoading(false);
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <div className="border rounded-lg p-2 hover:bg-gray-50">
        {isEditing ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Child's name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm flex items-center"
              >
                {isLoading ? (
                  <>
                    <span className="mr-1">Saving...</span>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </>
                ) : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-medium">{displayName}</h3>
            </div>
            <PrimaryParentOnly>
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-800"
                >
                  Delete
                </button>
              </div>
            </PrimaryParentOnly>
          </div>
        )}
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md border border-red-200">
            <h2 className="text-xl font-semibold mb-2 text-red-700">Delete dependent?</h2>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone. All data associated with this dependent will be permanently lost.
            </p>
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-6 text-sm">
              Warning: you are deleting "{displayName}".
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded text-sm bg-white hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded text-sm bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400"
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
